import { BALANCE, colOf } from '../constants';
import type { Simulation } from '../sim';
import { ORE_NONE, type Peep } from '../types';
import { digColumn, digWorkTiles } from '../world/terrain';
import { tierSpeed } from './tiers';

const scratchWorkTiles: number[] = [];

function digTicks(level: number, peep: Peep): number {
  return Math.max(10, Math.round((BALANCE.digTicksBase * (1 + BALANCE.digTicksDepthFactor * level)) / tierSpeed(peep)));
}

function reIdle(sim: Simulation, peep: Peep, orderCol: number): void {
  const order = sim.state.orders.get(orderCol);
  if (order && order.claimedBy === peep.id) order.claimedBy = null;
  peep.task = { kind: 'idle' };
  peep.path = null;
}

/** Start digs when miners arrive; tick digs; excavate + drop ore on completion. */
export function stepMining(sim: Simulation): void {
  const state = sim.state;
  const terrain = state.terrain;
  for (const peep of state.peeps) {
    if (peep.task.kind === 'toDig' && peep.path === null) {
      const col = peep.task.col;
      const order = state.orders.get(col);
      if (!order || order.claimedBy !== peep.id) {
        peep.task = { kind: 'idle' };
        continue;
      }
      const standCol = colOf(Math.floor(peep.x), Math.floor(peep.y));
      const workTiles = digWorkTiles(terrain, col, scratchWorkTiles);
      if (!workTiles.includes(standCol)) {
        // Arrived somewhere stale (terrain changed under us) — release and retry.
        reIdle(sim, peep, col);
        continue;
      }
      peep.task = { kind: 'digging', col, ticksLeft: digTicks(terrain.depth[col] ?? 0, peep) };
    } else if (peep.task.kind === 'digging') {
      const col = peep.task.col;
      const order = state.orders.get(col);
      if (!order || order.claimedBy !== peep.id) {
        peep.task = { kind: 'idle' };
        continue;
      }
      peep.task.ticksLeft -= 1;
      if (peep.task.ticksLeft > 0) continue;

      const dug = digColumn(terrain, col);
      sim.pushEvent({ kind: 'tileChanged', col });
      if (dug.type !== ORE_NONE && dug.amountDt > 0) {
        sim.state.town.oreMinedTotalDt += dug.amountDt;
        const pile = state.piles.get(col) ?? { col, ironDt: 0, goldDt: 0, claimedDt: 0 };
        if (dug.type === 1) pile.ironDt += dug.amountDt;
        else pile.goldDt += dug.amountDt;
        state.piles.set(col, pile);
      }
      if ((terrain.depth[col] ?? 0) >= order.targetDepth) {
        state.orders.delete(col);
      } else {
        order.claimedBy = null;
        order.suspended = false;
      }
      peep.task = { kind: 'idle' };
    }
  }
}
